import {
  ClientOrganizationProfileRoleEnum,
  ClientOrganizationRoleEnum
} from '@DigitaleDoerfer/digitale-doerfer-api';
import type { ClientPersonReference } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Divider, IconButton, Text } from 'react-native-paper';

import { MoreHorizontalIcon, PersonAddIcon } from '@/assets';
import { useGetOrganizationById } from '@/features/api/hooks/grapevine-organization/useGetOrganizationById';
import type { ClientOrganizationMember } from '@/features/api/hooks/grapevine-organization/useGetOrganizationMembers';
import { useGetOrganizationMembers } from '@/features/api/hooks/grapevine-organization/useGetOrganizationMembers';
import { useAppState } from '@/features/authorization/provider/useAppState';
import { PersonAvatar } from '@/features/media/components/avatar/PersonAvatar';
import { AddMemberModal } from '@/features/profile-setting/components/AddMemberModal';
import { MemberActionMenu } from '@/features/profile-setting/components/MemberActionMenu';
import type { ProfileSettingRoute } from '@/features/profile-setting/navigation/profileSettingRoute';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { NavigationBar } from '@/navigation/components/navbar/NavigationBar';
import type { AppNativeStackScreenProps } from '@/navigation/types/navigation';
import { GradientBackground } from '@/shared/components/GradientBackground';
import {
  OrganizationAccessProvider,
  useOrganizationAccess
} from '@/shared/components/organization';
import { OrganizationAccessState } from '@/shared/components/organization/organizationAccess';
import { StateView } from '@/shared/components/state-view/StateView';
import { hasItems } from '@/shared/utils/array';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

type Props = AppNativeStackScreenProps<
  typeof ProfileSettingRoute.OrganizationMembers
>;

const getFullName = (member: ClientOrganizationMember) =>
  [member.firstName, member.lastName].filter(Boolean).join(' ');

// PersonAvatar accepts ClientPersonReference; our member shape is structurally compatible.
const toPersonReference = (
  member: ClientOrganizationMember
): ClientPersonReference => ({
  id: member.id,
  firstName: member.firstName ?? undefined,
  lastName: member.lastName ?? undefined,
  profilePicture: member.profilePicture ?? undefined
});

const OrganizationMembersContent: FC<Props> = ({ route, navigation }) => {
  const theme = useAppTheme();
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization-members'
  });
  const { organizationId } = route.params;

  const { data: organization } = useGetOrganizationById(organizationId);
  const {
    data: members,
    isLoading,
    isRefetching,
    refetch
  } = useGetOrganizationMembers(organizationId);

  const styles = useMemo(() => makeStyles(theme), [theme]);

  const admins = members?.admins ?? [];
  const funkers = members?.funkers ?? [];
  // Management rights come from the organization access rules: a direct
  // OrganizationAdmin or any global organization admin may manage members.
  const currentUserIsAdmin =
    useOrganizationAccess({ roles: [ClientOrganizationRoleEnum.Admin] }) ===
    OrganizationAccessState.Granted;

  const [isAddModalVisible, setIsAddModalVisible] = useState(false);

  const handleAddMember = useCallback(() => {
    setIsAddModalVisible(true);
  }, []);
  const handleCloseAddModal = useCallback(() => {
    setIsAddModalVisible(false);
  }, []);

  useEffect(() => {
    navigation.setOptions({
      header: () => (
        <View style={styles.headerSpacer}>
          <NavigationBar
            title={t('title')}
            subTitle={organization?.name}
            buttons={
              currentUserIsAdmin
                ? [
                    {
                      name: t('buttons.addMember'),
                      icon: PersonAddIcon,
                      onPress: handleAddMember
                    }
                  ]
                : undefined
            }
          />
        </View>
      )
    });
  }, [
    currentUserIsAdmin,
    handleAddMember,
    navigation,
    organization?.name,
    styles,
    t
  ]);

  return (
    <GradientBackground>
      <StateView
        isLoading={isLoading}
        isRefetching={isRefetching}
        onRefetch={refetch}
        stateLabels={t('state', { returnObjects: true })}
        backgroundColor="transparent"
        contentContainerStyle={styles.content}
      >
        {hasItems(admins) && (
          <MemberSection
            organizationId={organizationId}
            title={t('sections.admins', { count: admins.length })}
            description={t('sectionDescriptions.admins')}
            members={admins}
            roleKey={ClientOrganizationProfileRoleEnum.Admin}
            isLastAdmin={admins.length === 1}
            currentUserIsAdmin={currentUserIsAdmin}
            actionsLabel={t('buttons.memberActions')}
          />
        )}
        {hasItems(funkers) && (
          <MemberSection
            organizationId={organizationId}
            title={t('sections.funkers', { count: funkers.length })}
            description={t('sectionDescriptions.funkers')}
            members={funkers}
            roleKey={ClientOrganizationProfileRoleEnum.Funker}
            isLastAdmin={false}
            currentUserIsAdmin={currentUserIsAdmin}
            actionsLabel={t('buttons.memberActions')}
          />
        )}
      </StateView>
      <AddMemberModal
        organizationId={organizationId}
        isVisible={isAddModalVisible}
        onClose={handleCloseAddModal}
      />
    </GradientBackground>
  );
};

export const OrganizationMembersScreen: FC<Props> = (props) => {
  const {
    route: {
      params: { organizationId }
    }
  } = props;
  const { data: organization } = useGetOrganizationById(organizationId);

  return (
    <OrganizationAccessProvider organization={organization}>
      <OrganizationMembersContent {...props} />
    </OrganizationAccessProvider>
  );
};

type MemberSectionProps = {
  organizationId: string;
  title: string;
  description: string;
  members: ClientOrganizationMember[];
  roleKey: ClientOrganizationProfileRoleEnum;
  /**
   * True for the admins section when only one admin exists. Passed straight to
   * every row so `MemberActionMenu` can pre-block leave/remove/demote actions.
   */
  isLastAdmin: boolean;
  /**
   * True when the current user is an admin of this org. Gates whether the
   * per-row "..." menu is rendered for other members' rows.
   */
  currentUserIsAdmin: boolean;
  actionsLabel: string;
};

const MemberSection: FC<MemberSectionProps> = ({
  organizationId,
  title,
  description,
  members,
  roleKey,
  isLastAdmin,
  currentUserIsAdmin,
  actionsLabel
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  return (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <Text variant="headlineSmall" style={styles.sectionTitle}>
          {title}
        </Text>
        <Text variant="bodyMedium" style={styles.sectionDescription}>
          {description}
        </Text>
      </View>
      <View style={styles.sectionBody}>
        {members.map((member, index) => (
          <View key={member.roleAssignmentId}>
            <MemberRow
              organizationId={organizationId}
              member={member}
              roleKey={roleKey}
              isLastAdmin={isLastAdmin}
              currentUserIsAdmin={currentUserIsAdmin}
              actionsLabel={actionsLabel}
            />
            {index < members.length - 1 && <Divider />}
          </View>
        ))}
      </View>
    </View>
  );
};

type MemberRowProps = {
  organizationId: string;
  member: ClientOrganizationMember;
  roleKey: ClientOrganizationProfileRoleEnum;
  isLastAdmin: boolean;
  currentUserIsAdmin: boolean;
  actionsLabel: string;
};

const MemberRow: FC<MemberRowProps> = ({
  organizationId,
  member,
  roleKey,
  isLastAdmin,
  currentUserIsAdmin,
  actionsLabel
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { userId } = useAppState();
  const [menuVisible, setMenuVisible] = useState(false);

  const isSelf = userId === member.id;
  // Funkers can only act on their own row (to leave the org).
  const showMenu = currentUserIsAdmin || isSelf;

  return (
    <View style={styles.row}>
      <PersonAvatar person={toPersonReference(member)} size={48} />
      <View style={styles.rowText}>
        <Text variant="titleMedium" numberOfLines={1} ellipsizeMode="tail">
          {getFullName(member)}
        </Text>
        {member.email && (
          <Text
            variant="bodyMedium"
            numberOfLines={1}
            ellipsizeMode="tail"
            style={styles.rowSubtitle}
          >
            {member.email}
          </Text>
        )}
      </View>
      {showMenu && (
        <MemberActionMenu
          organizationId={organizationId}
          member={member}
          currentRoleKey={roleKey}
          isLastAdmin={isLastAdmin}
          currentUserIsAdmin={currentUserIsAdmin}
          visible={menuVisible}
          onDismiss={() => setMenuVisible(false)}
          anchor={
            <IconButton
              accessibilityLabel={actionsLabel}
              accessibilityHint={actionsLabel}
              icon={() => (
                <MoreHorizontalIcon
                  fill={theme.colors.onSurface}
                  width={24}
                  height={24}
                />
              )}
              onPress={() => setMenuVisible(true)}
            />
          }
        />
      )}
    </View>
  );
};

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    content: { paddingBottom: 32, gap: 24 },
    headerSpacer: {
      paddingTop: 16,
      backgroundColor: theme.colors.surfaceContainer
    },
    section: { gap: 8 },
    sectionHeader: { paddingHorizontal: 16, paddingTop: 16, gap: 2 },
    sectionTitle: { color: theme.colors.onSurface },
    sectionDescription: { color: theme.colors.onSurfaceVariant },
    sectionBody: { backgroundColor: theme.colors.surfaceContainerLowest },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      paddingHorizontal: 16,
      paddingVertical: 12
    },
    rowText: { flex: 1, gap: 2 },
    rowSubtitle: { color: theme.colors.onSurfaceVariant }
  });
